/**
 * NoteCreateActionCard — read/edit view for an `ahoy_note` create action.
 *
 * Renders NoteCard with a synthesized Note from the action payload. Edit is
 * handled internally by NoteCard (the accordion Edit button is suppressed for
 * notes via isEditableAction). onEdit saves the updated values back to the
 * proposal action payload instead of the CRM store.
 *
 * Notes only: a proposed task renders through TaskCreateActionCard.
 */

import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import type { Note } from '@/lib/crm/record/memo/notes.types';
import { getMemoAssociationNames } from '@/lib/crm/record/memo/notes.utils';
import { NoteCard } from '@/lib/crm/record/notes/NoteCard';

import type {
  ObjectCreatedEntityPayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import type { ActionRendererProps } from '../../lib/proposals/registry/types';
import type { MergedAssociation } from '../../lib/proposals/utils/actionMerging';
import { extractProperties, wrapProperties } from './CreateActionCard.helpers';
import { formatValue } from './formatValue';
import { useActionPayloadSave } from './shared';

// Stable empty-array identity so the default `associations` prop doesn't
// change reference each render (keeps the synthesizeNote useMemo stable).
const EMPTY_ASSOCIATIONS: MergedAssociation[] = [];

const NOTE_OWNER_ASSOCIATION_NAME = getMemoAssociationNames('ahoy_note').owner;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Build a synthetic Note from an ahoy_note create-action payload so we can pass
 * it directly to NoteCard. The record doesn't exist in the CRM yet, so
 * entity_id is an empty string and timestamps are blank — NoteCard handles
 * these gracefully (no attribution row, no edit/delete timestamps).
 */
function synthesizeNote(
  action: ProposedAction<ObjectCreatedEntityPayload>
): Note {
  const flatProps = extractProperties(action.payload.payload.properties);
  return {
    entity_id: '',
    // Leave empty when the proposal payload has no title so the user is never
    // shown a placeholder string ('Note') that could be saved verbatim as the
    // actual title. NoteCard hides the title row when empty, matching the
    // canonical MemoForm initial-title contract.
    title: formatValue(flatProps['ahoy_title']) || '',
    content: formatValue(flatProps['ahoy_content']),
    created_at: '',
    updated_at: '',
    // Intentionally left null: a populated owner_entity_id would pre-fill
    // MemoForm's owner picker with the wrong user when editing a synthesized
    // note. The owner is surfaced for display via the `ownerName` prop instead.
    owner_entity_id: null,
    last_event_seq: 0,
    // `ahoy_note` no longer carries ahoy_date/ahoy_datetime (see #13931), so a
    // note payload never has a due date to surface. Notes have no stage
    // either (epic #13640).
    stage_entity_id: null,
    creator_entity_id: null,
    is_private: false,
  };
}

/**
 * Resolve the note owner's display name from the folded-in owner association.
 * The backend enriches association actions with the target entity's label, so
 * we read it straight off `enrichedData` rather than re-fetching the user.
 */
function resolveOwnerName(
  associations: MergedAssociation[]
): string | undefined {
  const ownerAssoc = associations.find(
    a => a.associationType === NOTE_OWNER_ASSOCIATION_NAME
  );
  return (
    ownerAssoc?.enrichedData?.target_object?.label ??
    ownerAssoc?.enrichedData?.object_label ??
    undefined
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

interface NoteCreateActionCardProps {
  action: ProposedAction<ObjectCreatedEntityPayload>;
  proposalId?: number;
  orgId?: number;
  onUpdate?: ActionRendererProps['onUpdate'];
  /**
   * Association siblings folded into this note's card (owner, parent link,
   * etc.). The owner is shown in NoteCard's footer; the rest are intentionally
   * not shown. Empty in non-merged render paths.
   */
  associations?: MergedAssociation[];
}

/**
 * Renders the note preview (NoteCard) for an ahoy_note create action.
 *
 * Edit mode is managed by NoteCard internally — clicking the pencil opens
 * MemoForm in-place. onEdit saves back to the proposal action payload (not
 * the CRM), so the same MemoForm used in the slideout drives proposal edits
 * with no duplication.
 */
export function NoteCreateActionCard({
  action,
  proposalId,
  orgId,
  onUpdate,
  associations = EMPTY_ASSOCIATIONS,
}: NoteCreateActionCardProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<Error | null>(null);

  const { savePayload } = useActionPayloadSave({
    orgId,
    proposalId,
    actionId: action.id,
    onUpdate,
  });

  const note = useMemo(() => synthesizeNote(action), [action]);
  const ownerName = useMemo(
    () => resolveOwnerName(associations),
    [associations]
  );

  const handleEdit = useCallback(
    async (
      _noteEntityId: string,
      title: string,
      content: string,
      opts?: {
        date?: string;
        ownerEntityId?: string;
        stageAssociation?: string | null;
        privacy?: 'private' | 'shared';
      }
    ) => {
      // Stage edits aren’t persisted to the proposal action payload — the
      // action is a single ahoy_object_created event whose payload schema
      // doesn’t carry associations. Accept the argument so it’s not silently
      // dropped against the form’s typed contract, then surface a user-facing
      // toast if the user attempted to convert it to a task.
      const { stageAssociation } = opts ?? {};
      if (!proposalId || !orgId) {
        return;
      }
      // Warn only on a real stage change. `ahoy_note` no longer carries
      // ahoy_date/ahoy_datetime (see #13931), and MemoForm hides its date
      // picker for notes, so a plain title/body edit stays silent instead of
      // toasting each time.
      const submittedStage = stageAssociation ?? null;
      const stageChanged = submittedStage !== (note.stage_entity_id ?? null);
      setIsSaving(true);
      setSaveError(null);
      try {
        const baseProps = extractProperties(action.payload.payload.properties);
        // Strip legacy due-date keys before re-spreading. `ahoy_note` no longer
        // carries ahoy_date/ahoy_datetime (see #13931), but a note proposal
        // created before that change can still hold them. Preserving them here
        // would re-persist invalid properties the backend rejects on execution.
        const {
          ahoy_date: _ahoy_date,
          ahoy_datetime: _ahoy_datetime,
          ...basePropsWithoutDue
        } = baseProps;
        const updatedProperties = wrapProperties({
          ...basePropsWithoutDue,
          ahoy_title: title,
          ahoy_content: content,
        });
        const updatedPayload: ObjectCreatedEntityPayload = {
          ...action.payload,
          payload: {
            ...action.payload.payload,
            properties: updatedProperties,
          },
        };
        await savePayload(updatedPayload);
        // Surface the dropped-stage-change warning only after the save
        // succeeds — the copy asserts the other details "were saved", so
        // emitting it before savePayload would lie to the user on failure.
        if (stageChanged) {
          toast.message(
            'Converting a note to a task isn’t supported in proposals yet. Only the note title and body were saved.'
          );
        }
      } catch (err) {
        setSaveError(err instanceof Error ? err : new Error('Save failed'));
      } finally {
        setIsSaving(false);
      }
    },
    [action, note.stage_entity_id, proposalId, orgId, savePayload]
  );

  // Readonly contexts (mode === 'readonly' / no editing) omit `onUpdate`.
  // Withhold `onEdit` in that case so NoteCard hides its edit pencil — the
  // accordion-level edit gating already removes the shell Edit button for
  // every memo entity (`isEditableAction`), but NoteCard's internal pencil
  // would otherwise let users open the form and attempt a save that has no
  // proposal update plumbing. Also gate on proposalId / orgId: handleEdit
  // short-circuits when either is missing.
  const canEdit = Boolean(onUpdate && proposalId && orgId);
  // Bail out cleanly when no org context is available. The card relies on
  // `orgId` downstream for entity-mention hydration (MarkdownContent); passing
  // `0` would drive org-scoped rendering / requests against an invalid org.
  if (!orgId) {
    return null;
  }
  return (
    <NoteCard
      note={note}
      orgId={orgId}
      ownerName={ownerName}
      canDelete={false}
      onEdit={canEdit ? handleEdit : undefined}
      isEditing={isSaving}
      editError={saveError}
      // A synthesized note has no real created/updated timestamps, so keep the
      // "created/edited X ago" attribution line hidden — showing it for a
      // proposal would be a meaningless "Unknown" row.
      showAttribution={false}
      // ...but DO surface the owner folded in from the proposal's sibling
      // association actions. The footer auto-hides when there's no owner.
      showFooter
    />
  );
}
